import React from 'react';
import { Button as AriaButton, Link } from 'react-aria-components';

import { PressEvent, Size } from '../../types';
import { ButtonSpinner, buttonStyles, isAgentWorking } from '../button/button';
import { TooltipSeat } from '../tooltip/tooltip';
import { TooltipSeatContext } from '../tooltip/tooltip-seat-context';

/**
 * ComplyKit IconButton: an action whose whole visible surface is a glyph.
 *
 * A square of the Button ramp, rendered from Button's own recipe (Button owns
 * the tv config; this file only sets shape: 'square'), so the two can never
 * drift in geometry, colour or states.
 *
 * The prop surface matches Button's (the same union of a link form and an
 * action form, the same autoFocus/isDisabled/id), with two deliberate
 * differences:
 *
 * - `label` is REQUIRED and there is no aria-label prop. An icon-only button
 *   has no visible text to name it, so the name is part of the API rather
 *   than an accessibility afterthought (the Primer/Atlassian contract;
 *   icon.md: never label the icon to name its button).
 * - The default variant is `ghost`: the census shows that is what icon
 *   buttons are in this product, chrome-free actions in toolbars, table rows
 *   and headers, not primary calls to action.
 * - The label tooltips NATIVELY, with no delay (the `tooltip` prop). The
 *   glyph is the whole visible surface, so the name must be one hover away,
 *   not half a second away; this is the instant tier of the two-tier delay
 *   policy (tooltip.md). Button gets no such pairing: it wears its name.
 */
interface IconButtonCommonProps {
  /**
   * The glyph: a heroicons 24/outline component, or a custom SVG drawn in
   * currentColor. The button sizes and colours it from its own recipe
   * (icon.md); the call site never does.
   */
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * The button's accessible name. Names the action, not the picture:
   * "Delete finding", never "Trash can". The icon itself stays hidden from
   * assistive technology; this is what a screen reader announces.
   */
  label: string;
  /**
   * Visual emphasis, the same axis as Button. Defaults to `ghost`, which is
   * what the product's icon buttons are by census.
   */
  variant?: 'solid' | 'soft' | 'outline' | 'ghost';
  /**
   * Recolours any variant onto a semantic scale, `critical` or `ai`. The
   * same union as Button, for the same reasons.
   */
  tone?: 'critical' | 'ai';
  /** Square side: sm 24px, md 32px, lg 40px, the Button ramp; md is the default. */
  size?: Size;
  /**
   * The built-in tooltip pairing. Left alone, the `label` shows as a tooltip
   * with NO delay, the icon-only standard (tooltip.md). A string shows that
   * text instead while `label` stays the accessible name; `false` opts out.
   * Inside an explicit ComplyKit Tooltip the pairing stands down on its own,
   * so a wrapping tooltip (the disabled-reason idiom) never doubles up.
   */
  tooltip?: string | false;
  /**
   * Whether the button is disabled. Prefer `isPending` for in-flight work:
   * disabling a focused button drops focus.
   *
   * With the built-in tooltip pairing active (or inside an explicit ComplyKit
   * Tooltip), a disabled icon button takes the aria-disabled form: still
   * focusable and hover-capable so its name stays one hover away, with every
   * press suppressed and `type="submit"` rendered as `button` (tooltip.md).
   * With `tooltip={false}` and no wrapping Tooltip it keeps the native
   * disabled attribute.
   */
  isDisabled?: boolean;
  /** Whether the button should receive focus on mount. */
  autoFocus?: boolean;
  /** The id of the button element. */
  id?: string;
}

/* The same union as Button: somewhere to go OR something to do, and the
 * invalid combinations (a navigation with an in-flight state, an action with
 * a target) do not compile. */
interface IconButtonLinkProps extends IconButtonCommonProps {
  /** Where the button goes. Renders a real link with the button's
   * appearance, exactly as Button infers its element. */
  href: string;
  /** Link target, for the rare action that opens away from the app. */
  target?: '_blank';
  onPress?: never;
  isPending?: never;
  type?: never;
}

interface IconButtonActionProps extends IconButtonCommonProps {
  /**
   * What the button does: an action on the page rather than a navigation.
   * Presses do not bubble, and the event carries the modifier keys held
   * during the press (see `PressEvent`).
   */
  onPress?: (e: PressEvent) => void;
  /**
   * The in-flight state of the button's own action: the glyph hides but
   * keeps its space, a centred spinner appears, the button stays focusable,
   * exposes aria-disabled, ignores presses and announces progress.
   */
  isPending?: boolean;
  /** The button's form behaviour. Defaults to `button`. */
  type?: 'button' | 'submit' | 'reset';
  href?: never;
  target?: never;
}

export type IconButtonProps = IconButtonLinkProps | IconButtonActionProps;

export const IconButton = React.forwardRef<HTMLElement, IconButtonProps>(function IconButton(
  {
    icon: IconGlyph,
    label,
    variant = 'ghost',
    tone,
    size = 'md',
    tooltip,
    isPending = false,
    isDisabled = false,
    onPress,
    href,
    target,
    type = 'button',
    ...props
  },
  ref,
) {
  // A navigation has no in-flight state; see Button.
  const pending = isPending && !href;

  /* The built-in pairing stands down inside an explicit Tooltip, so the
   * outer one (a disabled-reason swap, a richer description) wins the seat
   * instead of doubling up. */
  const inTooltipSeat = React.useContext(TooltipSeatContext);
  const paired = tooltip !== false && !inTooltipSeat;

  /* Disabled in ANY tooltip seat, the built-in pairing included, takes the
   * aria-disabled form so the name stays one hover away; see Button for the
   * mechanism (React Aria's pending machinery, visuals off). Links are
   * untouched: a disabled link-button is a bare span. */
  const seated = isDisabled && (inTooltipSeat || paired) && !href;

  const resolvedTone = tone ?? (variant === 'solid' ? 'accent' : 'neutral');
  const { root, content, icon, pendingOverlay, spinner } = buttonStyles({
    variant,
    tone: resolvedTone,
    size,
    shape: 'square',
    pending,
    agentWorking: isAgentWorking(pending, resolvedTone, variant),
    ariaDisabled: seated,
  });

  const body = (
    <>
      <span className={content()}>
        <IconGlyph aria-hidden="true" focusable="false" className={icon()} />
      </span>
      {pending && <ButtonSpinner overlay={pendingOverlay()} disc={spinner()} />}
    </>
  );

  const element = href ? (
    <Link
      {...props}
      ref={ref as React.Ref<HTMLAnchorElement>}
      isDisabled={isDisabled}
      href={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      aria-label={label}
      className={root()}
      data-component="icon-button"
      data-tone={resolvedTone}
    >
      {body}
    </Link>
  ) : (
    <AriaButton
      {...props}
      ref={ref as React.Ref<HTMLButtonElement>}
      isDisabled={seated ? undefined : isDisabled}
      onPress={seated ? undefined : onPress}
      type={seated && type === 'submit' ? 'button' : type}
      isPending={pending || seated}
      aria-label={label}
      className={root()}
      data-component="icon-button"
      data-tone={resolvedTone}
    >
      {body}
    </AriaButton>
  );

  if (!paired) return element;

  /* delay 0 is the pairing's whole point: the label IS the button's visible
   * text, so it shows as immediately as a label would. Placement is fixed at
   * top, the reading position for a control's name. */
  return (
    <TooltipSeat content={tooltip ?? label} placement="top" delay={0}>
      {element}
    </TooltipSeat>
  );
});
